import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

const ZOOM_MIN = 1;
const ZOOM_MAX = 5;

type Props = {
  children: React.ReactNode;
  /** 값이 바뀌면 확대/이동 초기화 (CCTV 전환 등) */
  resetKey?: string | number;
};

/**
 * 휠 확대 · 드래그 이동 · 핀치 줌 (모니터링 팝업 rot-zoom 과 동일 동작)
 */
const ZoomPanViewport: React.FC<Props> = ({ children, resetKey }) => {
  const viewportRef = useRef<HTMLDivElement>(null);
  const [zoomScale, setZoomScale] = useState(ZOOM_MIN);
  const [panX, setPanX] = useState(0);
  const [panY, setPanY] = useState(0);
  const [isPanning, setIsPanning] = useState(false);

  const zoomScaleRef = useRef(zoomScale);
  const panXRef = useRef(panX);
  const panYRef = useRef(panY);
  const isPanningRef = useRef(false);
  const panDragStart = useRef({ x: 0, y: 0, ox: 0, oy: 0 });
  const pinchRef = useRef({
    active: false,
    startDist: 1,
    startScale: 1,
    startPanX: 0,
    startPanY: 0,
    midX: 0,
    midY: 0,
  });

  useEffect(() => {
    zoomScaleRef.current = zoomScale;
  }, [zoomScale]);
  useEffect(() => {
    panXRef.current = panX;
  }, [panX]);
  useEffect(() => {
    panYRef.current = panY;
  }, [panY]);

  const clampPan = useCallback((scale: number, x: number, y: number) => {
    const el = viewportRef.current;
    if (!el || scale <= ZOOM_MIN) return { x: 0, y: 0 };
    const w = el.clientWidth || 0;
    const h = el.clientHeight || 0;
    const maxPanX = (w * (scale - 1)) / 2;
    const maxPanY = (h * (scale - 1)) / 2;
    return {
      x: Math.min(maxPanX, Math.max(-maxPanX, x)),
      y: Math.min(maxPanY, Math.max(-maxPanY, y)),
    };
  }, []);

  const resetZoom = useCallback(() => {
    setZoomScale(ZOOM_MIN);
    setPanX(0);
    setPanY(0);
    setIsPanning(false);
    isPanningRef.current = false;
    pinchRef.current.active = false;
  }, []);

  useEffect(() => {
    resetZoom();
  }, [resetKey, resetZoom]);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;

    const onWheel = (ev: WheelEvent) => {
      ev.preventDefault();
      const rect = viewport.getBoundingClientRect();
      const cx = rect.width / 2;
      const cy = rect.height / 2;
      const mx = ev.clientX - rect.left - cx;
      const my = ev.clientY - rect.top - cy;
      const oldScale = zoomScaleRef.current;
      const factor = ev.deltaY > 0 ? 0.9 : 1.1;
      let newScale = oldScale * factor;
      newScale = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, newScale));
      if (Math.abs(newScale - oldScale) < 0.0001) return;
      if (newScale <= ZOOM_MIN) {
        resetZoom();
        return;
      }
      const nx = mx - ((mx - panXRef.current) * newScale) / oldScale;
      const ny = my - ((my - panYRef.current) * newScale) / oldScale;
      const clamped = clampPan(newScale, nx, ny);
      setZoomScale(newScale);
      setPanX(clamped.x);
      setPanY(clamped.y);
    };

    const onMouseDown = (ev: MouseEvent) => {
      if (ev.button !== 0 || zoomScaleRef.current <= ZOOM_MIN) return;
      isPanningRef.current = true;
      setIsPanning(true);
      panDragStart.current = {
        x: ev.clientX,
        y: ev.clientY,
        ox: panXRef.current,
        oy: panYRef.current,
      };
      ev.preventDefault();
    };

    const onMouseMove = (ev: MouseEvent) => {
      if (!isPanningRef.current) return;
      const { x, y, ox, oy } = panDragStart.current;
      const nx = ox + (ev.clientX - x);
      const ny = oy + (ev.clientY - y);
      const clamped = clampPan(zoomScaleRef.current, nx, ny);
      setPanX(clamped.x);
      setPanY(clamped.y);
    };

    const onMouseUp = () => {
      if (!isPanningRef.current) return;
      isPanningRef.current = false;
      setIsPanning(false);
    };

    const touchDistance = (t0: Touch, t1: Touch) => {
      const dx = t0.clientX - t1.clientX;
      const dy = t0.clientY - t1.clientY;
      return Math.sqrt(dx * dx + dy * dy);
    };

    const onTouchStart = (ev: TouchEvent) => {
      if (ev.touches.length === 2) {
        pinchRef.current.active = true;
        isPanningRef.current = false;
        setIsPanning(false);
        const t0 = ev.touches[0];
        const t1 = ev.touches[1];
        const rect = viewport.getBoundingClientRect();
        const cx = rect.width / 2;
        const cy = rect.height / 2;
        pinchRef.current = {
          active: true,
          startDist: Math.max(1, touchDistance(t0, t1)),
          startScale: zoomScaleRef.current,
          startPanX: panXRef.current,
          startPanY: panYRef.current,
          midX: (t0.clientX + t1.clientX) / 2 - rect.left - cx,
          midY: (t0.clientY + t1.clientY) / 2 - rect.top - cy,
        };
        ev.preventDefault();
      } else if (ev.touches.length === 1 && zoomScaleRef.current > ZOOM_MIN) {
        pinchRef.current.active = false;
        const t = ev.touches[0];
        isPanningRef.current = true;
        setIsPanning(true);
        panDragStart.current = {
          x: t.clientX,
          y: t.clientY,
          ox: panXRef.current,
          oy: panYRef.current,
        };
        ev.preventDefault();
      }
    };

    const onTouchMove = (ev: TouchEvent) => {
      if (pinchRef.current.active && ev.touches.length === 2) {
        const t0 = ev.touches[0];
        const t1 = ev.touches[1];
        const dist = Math.max(1, touchDistance(t0, t1));
        const p = pinchRef.current;
        let newScale = p.startScale * (dist / p.startDist);
        newScale = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, newScale));
        if (newScale <= ZOOM_MIN) {
          resetZoom();
          ev.preventDefault();
          return;
        }
        const nx =
          p.midX - ((p.midX - p.startPanX) * newScale) / p.startScale;
        const ny =
          p.midY - ((p.midY - p.startPanY) * newScale) / p.startScale;
        const clamped = clampPan(newScale, nx, ny);
        setZoomScale(newScale);
        setPanX(clamped.x);
        setPanY(clamped.y);
        ev.preventDefault();
      } else if (isPanningRef.current && ev.touches.length === 1) {
        const t = ev.touches[0];
        const { x, y, ox, oy } = panDragStart.current;
        const nx = ox + (t.clientX - x);
        const ny = oy + (t.clientY - y);
        const clamped = clampPan(zoomScaleRef.current, nx, ny);
        setPanX(clamped.x);
        setPanY(clamped.y);
        ev.preventDefault();
      }
    };

    const onTouchEnd = (ev: TouchEvent) => {
      if (ev.touches.length < 2) pinchRef.current.active = false;
      if (ev.touches.length === 0) {
        isPanningRef.current = false;
        setIsPanning(false);
      }
    };

    viewport.addEventListener('wheel', onWheel, { passive: false });
    viewport.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    viewport.addEventListener('touchstart', onTouchStart, { passive: false });
    viewport.addEventListener('touchmove', onTouchMove, { passive: false });
    viewport.addEventListener('touchend', onTouchEnd);
    viewport.addEventListener('touchcancel', onTouchEnd);

    return () => {
      viewport.removeEventListener('wheel', onWheel);
      viewport.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      viewport.removeEventListener('touchstart', onTouchStart);
      viewport.removeEventListener('touchmove', onTouchMove);
      viewport.removeEventListener('touchend', onTouchEnd);
      viewport.removeEventListener('touchcancel', onTouchEnd);
    };
  }, [clampPan, resetZoom]);

  const zoomed = zoomScale > ZOOM_MIN + 0.001;

  return (
    <Box
      ref={viewportRef}
      sx={{
        position: 'absolute',
        inset: 0,
        overflow: 'hidden',
        touchAction: 'none',
        userSelect: 'none',
        cursor: isPanning ? 'grabbing' : zoomed ? 'grab' : 'default',
      }}
    >
      {zoomed ? (
        <Tooltip title="원래 크기">
          <IconButton
            size="small"
            onClick={resetZoom}
            aria-label="원래 크기"
            sx={{
              position: 'absolute',
              top: 10,
              right: 10,
              zIndex: 18,
              bgcolor: 'rgba(34,34,34,0.92)',
              color: '#fff',
              border: '1px solid #666',
              '&:hover': { bgcolor: 'rgba(60,60,60,0.95)' },
            }}
          >
            <RestartAltIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : null}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          transformOrigin: 'center center',
          willChange: 'transform',
          transform: `translate(${panX}px, ${panY}px) scale(${zoomScale})`,
          '& > *': { height: '100% !important', minHeight: '100%' },
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default ZoomPanViewport;
